// Shaping of the spot-rate engine's stored quotes (migration 023) into what a customer reads: FCL offers with
// the three container sizes side by side, and an LCL ladder by size with an estimate for the customer's cargo.
// PURE and dependency-free on purpose: the REST route GET /lane/spot (and so the MCP tool) and the web board import
// this one file, so the browser and an agent can never disagree about what the stored quotes mean.

export type FclBasis = '20ST' | '40ST' | '40HC'
export interface SpotQuote {
  mode: 'FCL' | 'LCL'
  basis: string
  provider_id: string
  provider: string
  carrier: string | null
  amount: number
  currency: string
  transit_days: number | null
  departure: string | null
  valid_until: string | null
  includes: string | null
  badge: string | null
  source_url: string | null
  fetched_at: string
}

// ── FCL: one row per offer, the three container sizes side by side ───────────────────────────────

export interface FclOffer {
  key: string
  provider: string
  carrier: string | null
  prices: Record<FclBasis, number | null>
  transitDays: number | null
  departure: string | null
  validUntil: string | null
  includes: string | null
  badge: string | null
  sourceUrl: string | null
}

const FCL: ReadonlyArray<FclBasis> = ['20ST', '40ST', '40HC']

/**
 * The engine answers one container size per question, so the same offer arrives up to three times. Rows with
 * the same provider, carrier and departure are one offer; each size fills its own price (the lowest if a
 * size appears twice). Cheapest 40' HC first, then 40', then 20', then name; offers with no price for the size
 * sorted on go last.
 */
export function fclOffers(rows: ReadonlyArray<SpotQuote>): FclOffer[] {
  const by = new Map<string, FclOffer>()
  for (const r of rows) {
    if (r.mode !== 'FCL' || !FCL.includes(r.basis as FclBasis)) continue
    const key = [r.provider_id, r.carrier ?? '', r.departure ?? ''].join('|')
    let o = by.get(key)
    if (!o) {
      o = {
        key,
        provider: r.provider,
        carrier: r.carrier,
        prices: { '20ST': null, '40ST': null, '40HC': null },
        transitDays: r.transit_days,
        departure: r.departure,
        validUntil: r.valid_until,
        includes: r.includes,
        badge: r.badge,
        sourceUrl: r.source_url,
      }
      by.set(key, o)
    }
    const b = r.basis as FclBasis
    const cur = o.prices[b]
    if (cur == null || r.amount < cur) o.prices[b] = r.amount
    // The earliest validity is the one that bounds the offer.
    if (r.valid_until && (!o.validUntil || r.valid_until < o.validUntil))
      o.validUntil = r.valid_until
    o.transitDays ??= r.transit_days
    o.includes ??= r.includes
  }
  const rank = (o: FclOffer) =>
    o.prices['40HC'] ?? o.prices['40ST'] ?? o.prices['20ST'] ?? Number.POSITIVE_INFINITY
  return [...by.values()].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      a.provider.localeCompare(b.provider) ||
      (a.carrier ?? '').localeCompare(b.carrier ?? '')
  )
}

/**
 * Does the quote's own text say that origin / destination (local) charges are outside the price? true: outside,
 * false: inside, null: it does not say. Read from the source's wording, in three real shapes:
 *   SoFreight: "... local charges (BKF/THC/OC/...) not included"        -> outside
 *   Freightos: "Port-to-port ocean freight, origin/destination port charges excluded" -> outside
 *   Cogoport:  "Ocean freight USD 1536 plus origin and destination local charges 283.39" (the total is the sum)
 *              -> inside. It once read as "outside" because of the word "plus": a customer would have been told
 *              that the cheapest offer excludes charges it includes.
 * An explicit exclusion wins over an inclusion phrase.
 */
export function localChargesExcluded(includes: string | null | undefined): boolean | null {
  if (!includes) return null
  const t = includes.toLowerCase()
  if (/not included|charges excluded|\bexcluded\b|excluding|not covered|extra/.test(t)) return true
  if (
    /plus (origin|destination)[^.]*charges|including (local|origin|destination)|all[- ]in\b|door[- ]to[- ]door/.test(
      t
    )
  )
    return false
  return null
}

// ── LCL: a ladder by size, because the price per cubic metre falls steeply with the shipment ─────

export interface LclBandSpec {
  basis: 'LCL-1' | 'LCL-3' | 'LCL-10'
  /** Chargeable volume the band was priced for: cubic metres, or tonnes if heavier (1 W/M = 1 m3 or 1 t). */
  wm: number
  /** The reference cargo density the band was priced at. */
  kgPerCbm: number
}
export const LCL_BANDS: ReadonlyArray<LclBandSpec> = [
  { basis: 'LCL-1', wm: 1, kgPerCbm: 500 },
  { basis: 'LCL-3', wm: 3, kgPerCbm: 500 },
  { basis: 'LCL-10', wm: 10, kgPerCbm: 500 },
]

export interface LclBand extends LclBandSpec {
  /** Cheapest offer in the band; null when nobody quoted this size. */
  cheapest: SpotQuote | null
  offers: number
  perWm: number | null
  transit: [number, number] | null
}

export function lclLadder(rows: ReadonlyArray<SpotQuote>): LclBand[] {
  return LCL_BANDS.map((spec) => {
    const q = rows
      .filter((r) => r.mode === 'LCL' && r.basis === spec.basis)
      .sort((a, b) => a.amount - b.amount)
    const days = q.map((r) => r.transit_days).filter((d): d is number => d != null)
    return {
      ...spec,
      cheapest: q[0] ?? null,
      offers: q.length,
      perWm: q[0] ? Math.round(q[0].amount / spec.wm) : null,
      transit: days.length ? [Math.min(...days), Math.max(...days)] : null,
    }
  })
}

/** Chargeable weight/measurement: the greater of cubic metres and tonnes. */
export const chargeableWm = (cbm: number, kg: number): number => Math.max(cbm, kg / 1000)

export type LclEstimate =
  | { kind: 'band'; band: LclBand; wm: number; price: number; heavy: boolean }
  | { kind: 'over'; wm: number }
  | { kind: 'none' }

/**
 * The cheapest quote for the smallest priced size that covers the customer's cargo. It is the price of that whole
 * size ("up to 3 cbm"), not an interpolation: LCL is priced per shipment and a smaller shipment does not cost
 * proportionally less. `heavy` is true when the cargo weighs more than the band's reference density: a dense
 * cargo is priced higher (measured: 1 cbm at 300 kg 299 to 355, at 1000 kg 355 to 695), so the figure is a floor.
 */
export function lclEstimate(ladder: ReadonlyArray<LclBand>, cbm: number, kg: number): LclEstimate {
  if (!(cbm > 0) || !(kg >= 0) || !Number.isFinite(cbm) || !Number.isFinite(kg))
    return { kind: 'none' }
  const wm = chargeableWm(cbm, kg)
  const priced = ladder.filter((b) => b.cheapest)
  const band = priced.find((b) => b.wm >= wm)
  if (!band || !band.cheapest) return priced.length ? { kind: 'over', wm } : { kind: 'none' }
  return {
    kind: 'band',
    band,
    wm,
    price: band.cheapest.amount,
    heavy: kg > band.wm * band.kgPerCbm,
  }
}

// ── The REST / MCP answer for one lane ──────────────────────────────────────────────────────────

export type CoversLocalCharges = 'origin_destination_included' | 'ocean_freight_only' | 'not_stated'
const covers = (includes: string | null): CoversLocalCharges => {
  const x = localChargesExcluded(includes)
  return x === true
    ? 'ocean_freight_only'
    : x === false
      ? 'origin_destination_included'
      : 'not_stated'
}

export interface LaneSpotAnswer {
  pol: string
  pod: string
  /** Present only when a requested port is an alias (Shenzhen CNSZX is served as Yantian CNYTN). */
  aliases?: Array<{ requested: string; served_as: string }>
  /** queued: registered, first fetch pending (within the hour). watching: fetched before. */
  state: 'queued' | 'watching' | 'capped' | 'invalid' | 'unknown'
  fetched_at: string | null
  fcl: Array<{
    source: string
    carrier: string | null
    prices_usd: Record<FclBasis, number | null>
    transit_days: number | null
    departure: string | null
    valid_until: string | null
    covers: CoversLocalCharges
    details: string | null
    badge: string | null
    source_url: string | null
  }>
  lcl: {
    /** The cargo density the bands were priced at. Heavier cargo can cost more than the band price. */
    reference_kg_per_m3: number
    bands: Array<{
      up_to_wm: number
      from_usd: number | null
      per_wm_usd: number | null
      offers: number
      transit_days: [number, number] | null
      valid_until: string | null
      source: string | null
      covers: CoversLocalCharges
    }>
    estimate:
      | null
      | {
          kind: 'band'
          chargeable_wm: number
          up_to_wm: number
          from_usd: number
          heavier_than_reference: boolean
        }
      | { kind: 'over_largest_band'; chargeable_wm: number }
  }
  notes: string[]
}

/**
 * The stored quotes of one lane as one answer. `cargo` (cubic metres and kilos) adds an LCL estimate. Prices
 * are as the sources quote them, so each offer says what it covers; sources do not all quote on the same basis,
 * and the notes say so instead of leaving the reader to compare unlike figures.
 */
export function shapeLaneSpot(
  rows: ReadonlyArray<SpotQuote>,
  lane: {
    pol: string
    pod: string
    state?: LaneSpotAnswer['state']
    aliases?: Array<{ requested: string; served_as: string }>
  },
  cargo?: { cbm: number; kg: number }
): LaneSpotAnswer {
  const fetched = rows
    .map((r) => r.fetched_at)
    .filter(Boolean)
    .sort()
  const ladder = lclLadder(rows)
  const est = cargo ? lclEstimate(ladder, cargo.cbm, cargo.kg) : null
  const notes = [
    'Prices are USD per container (FCL) or per shipment (LCL) as each source quotes them. Sources do not all quote the same basis: `covers` says whether origin and destination charges are in the price, so compare only offers with the same `covers`.',
    'LCL is priced per shipment and falls steeply per cubic metre as the shipment grows; 1 W/M is 1 m3 or 1 tonne, whichever is greater. A band price is for the whole band, not an interpolation.',
  ]
  if (lane.aliases?.length)
    notes.unshift(
      `${lane.aliases.map((a) => `${a.requested} is served as ${a.served_as}`).join('; ')}: the same lane, the same rates, always.`
    )
  if (rows.length === 0)
    notes.unshift(
      lane.state === 'capped'
        ? 'No spot rates for this lane: spot tracking is not accepting new lanes right now.'
        : 'No spot rates are stored for this lane yet. It is registered and is fetched within the hour, then refreshed daily.'
    )
  return {
    pol: lane.pol,
    pod: lane.pod,
    ...(lane.aliases?.length ? { aliases: lane.aliases } : {}),
    state: lane.state ?? (rows.length ? 'watching' : 'unknown'),
    fetched_at: fetched.length ? (fetched[fetched.length - 1] as string) : null,
    fcl: fclOffers(rows).map((o) => ({
      source: o.provider,
      carrier: o.carrier,
      prices_usd: o.prices,
      transit_days: o.transitDays,
      departure: o.departure,
      valid_until: o.validUntil,
      covers: covers(o.includes),
      details: o.includes,
      badge: o.badge,
      source_url: o.sourceUrl,
    })),
    lcl: {
      reference_kg_per_m3: LCL_BANDS[0]?.kgPerCbm ?? 500,
      bands: ladder.map((b) => ({
        up_to_wm: b.wm,
        from_usd: b.cheapest?.amount ?? null,
        per_wm_usd: b.perWm,
        offers: b.offers,
        transit_days: b.transit,
        valid_until: b.cheapest?.valid_until ?? null,
        source: b.cheapest?.provider ?? null,
        covers: covers(b.cheapest?.includes ?? null),
      })),
      estimate:
        est && est.kind === 'band'
          ? {
              kind: 'band',
              chargeable_wm: Math.round(est.wm * 100) / 100,
              up_to_wm: est.band.wm,
              from_usd: est.price,
              heavier_than_reference: est.heavy,
            }
          : est && est.kind === 'over'
            ? { kind: 'over_largest_band', chargeable_wm: Math.round(est.wm * 100) / 100 }
            : null,
    },
    notes,
  }
}
